import axiosInstance from "./axios";
import { ChatMessage } from "@/types";

// ─── Helper: extract User_id from JWT ─────────────────────────────────────────
function getUserIdFromToken(): string {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No auth token found");
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        // Common JWT claim names for user id
        return payload.id || payload.sub || payload.user_id || payload.User_id || "";
    } catch {
        throw new Error("Failed to decode auth token");
    }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StartReturnRequest {
    order_id: string;
    product_id: string;
    customer_address?: Record<string, unknown>;
    pickup_notes?: string;
    additional_data?: Record<string, unknown>;
}

export interface StartReturnResponse {
    message: string;
    return_id: string;
    existing_chat?: ChatMessage[];
    status?: string;
    submitted?: boolean;
    ai_evaluation?: any;
    ai_confidence?: number;
    category?: string;
    reason?: string;
}

export interface ChatForReturnRequest {
    return_id: string;
    messagesArr: ChatMessage[];
}

export interface ChatForReturnResponse {
    success: boolean;
    message: string;
    message_id?: string;
    case_closed?: boolean;
    is_ready_for_resolution?: boolean;
    evaluation_triggered?: boolean;
    suggested_products?: any[];
}

export interface SubmitReturnRequest {
    return_id: string;
    customer_id: string;
}

export interface SubmitReturnResponse {
    message: string;
    data: {
        id: string;
        order_id: string;
        customer_id: string;
        merchant_id: string;
        product_id: string;
        status: string;
        return_type: string | null;
        submitted: boolean;
        updated_at: string;
    }; 
}

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * Start an order return request.
 * POST /customer/StartOrderReturnRequest
 */
export async function startOrderReturnRequest(
    payload: StartReturnRequest,
): Promise<StartReturnResponse> {
    const userId = getUserIdFromToken();
    const { data } = await axiosInstance.post<StartReturnResponse>(
        "/api/customer/StartOrderReturnRequest",
        {
            User_id: userId,
            ...payload,
        },
    );
    return data;
}

/**
 * Send a chat message to Anaya for return flow.
 * POST /customer/chatToAiForReturn
 */
export async function chatToAiForReturn(
    payload: ChatForReturnRequest,
): Promise<ChatForReturnResponse> {
    const userId = getUserIdFromToken();
    const { data } = await axiosInstance.post<ChatForReturnResponse>(
        "/api/triage/chat_for_return",
        {
            User_id: userId,
            ...payload,
        },
    );
    return data;
}

/**
 * Submit the return request (marks as submitted).
 * POST /customer/submitCancelRequest
 */
export async function submitReturnRequest(
    returnId: string,
    payload: {
        selected_solution?: string | null;
        customer_address?: any | null;
        replacement_product_id?: string | null;
        replacement_variant_id?: string | null;
        selected_variant_id?: string | null;
        selected_refund_method?: string | null;
        status?: string | null;
        closure_reason?: string | null;
        internal_closure_notes?: string | null;
        reason?: string | null;
    } = {}
): Promise<SubmitReturnResponse> {
    const userId = getUserIdFromToken();
    const { data } = await axiosInstance.post<SubmitReturnResponse>(
        "/api/customer/submitCancelRequest",
        {
            return_id: returnId,
            customer_id: userId,
            selected_solution: payload.selected_solution,
            selected_refund_method: payload.selected_refund_method,
            status: payload.status,
            customer_address: payload.customer_address,
            replacement_product_id: payload.replacement_product_id,
            replacement_variant_id: payload.replacement_variant_id,
            selected_variant_id: payload.selected_variant_id,
            closure_reason: payload.closure_reason,
            internal_closure_notes: payload.internal_closure_notes,
            reason: payload.reason,
        },
    );
    return data;
}

/**
 * Upload evidence for a return.
 * POST /customer/return/uploadEvidence
 */
export async function uploadReturnEvidence(
    formData: FormData,
): Promise<any> {
    const { data } = await axiosInstance.post(
        "/api/customer/return/uploadEvidence",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
}

/**
 * AI evaluation of return legitimacy and solution generation.
 * POST /api/customer/evaluateReturnIssue
 */
export async function evaluateReturnIssue(
    returnId: string,
): Promise<any> {
    const userId = getUserIdFromToken();
    const { data } = await axiosInstance.post(
        "/api/customer/evaluateReturnIssue",
        {
            return_id: returnId,
            customer_id: userId,
        },
    );
    return data;
}

/**
 * Fetch return data by order_id.
 * POST /api/customer/getReturnData
 */
export async function getReturnDataByOrderId(orderId: string): Promise<any> {
    const userId = getUserIdFromToken();
    const { data } = await axiosInstance.post(
        "/api/customer/getReturnData",
        {
            order_id: orderId,
            User_id: userId,
        },
    );
    return data;
}

/**
 * Fetch all variants (sizes/colors) for a specific product.
 * POST /api/customer/getProductVariants
 */
export async function getProductVariants(productId: string): Promise<any[]> {
    try {
        const response = await axiosInstance.post<{
            success: boolean;
            data: any[];
        }>("/api/customer/getProductVariants", {
            product_id: productId,
        });
        return response.data.data;
    } catch (err: any) {
        console.error("Error fetching product variants:", err);
        throw err;
    }
}

/**
 * Fetch full return onboarding data (authenticated).
 * POST /api/customer/get-return-onboarding-data
 */
export async function getReturnOnboardingData(orderId: string): Promise<any> {
    const { data } = await axiosInstance.post(
        "/api/customer/get-return-onboarding-data",
        {
            order_id: orderId,
        },
    );
    return data;
}
